import { Box, Dialog } from '@mui/material';
import { Fragment, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

import { EmptyList } from 'src/components/EmptyList';
import { BellIcon } from 'src/components/icons';
import { selectAccessToken } from 'src/services/auth';
import { config } from 'src/services/config';
import { useSelector } from 'src/services/redux';
import { useDispatch } from 'src/services/redux';
import { isEmpty } from 'src/utils/common';
import {
  useNotificationsUnreadCountQuery,
  userApi,
  userSharedListTag,
} from '../api';
import { useUnreadNotifications } from '../hooks/useUnreadNotifications';
import { UserNotification } from '../types';
import { NotificationItem } from './NotificationItem';

const SHARE_EVENT = 'SHARE';

const UnreadNotificationsList = (props: {
  onClickItem?: () => void;
  unreadCount?: number;
}) => {
  const { isLoading, notifications } = useUnreadNotifications();

  return (
    <Box sx={{ maxHeight: 400, overflowY: 'auto', width: '100%' }}>
      {isEmpty(notifications) ? (
        <Box>
          <EmptyList isLoading={isLoading} />
        </Box>
      ) : (
        <>
          {notifications.map((notification, i) => (
            <Fragment key={notification.id}>
              <NotificationItem
                onClick={props.onClickItem}
                notification={notification}
              />
              {i === notifications.length - 1 ? null : (
                <Box
                  sx={{
                    backgroundColor: '#9AB1C0',
                    height: '1px',
                    margin: '5px 0',
                    width: '100%',
                  }}
                />
              )}
            </Fragment>
          ))}
        </>
      )}
    </Box>
  );
};

export const Notifications = () => {
  const dispatch = useDispatch();

  const { data: notificationsUnreadCount } = useNotificationsUnreadCountQuery();

  const [isNotificationsListOpen, setNotificationsListOpen] = useState(false);
  const [newNotification, setNewNotification] = useState<UserNotification>();

  const accessToken = useSelector(selectAccessToken);
  const timeoutRef = useRef<any>();

  useEffect(() => {
    const socket = io(config.apiUrl as string, {
      query: { token: accessToken },
      transports: ['websocket'],
      withCredentials: true,
    });

    socket.on(SHARE_EVENT, (message: UserNotification) => {
      console.log('message', message);

      setNewNotification(message);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setNewNotification(undefined);
      }, 5000);

      dispatch(userApi.util.invalidateTags([userSharedListTag]));

      dispatch(
        userApi.util.updateQueryData(
          'notificationsUnreadCount',
          undefined,
          (draft) => draft + 1,
        ),
      );
    });

    return () => {
      socket.off(SHARE_EVENT);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [accessToken, dispatch, setNewNotification]);

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        margin: '0 10px',
        padding: '10px',
      }}
    >
      <BellIcon
        onClick={() => {
          setNotificationsListOpen(true);
        }}
        stroke="#9AB1C0"
        strokeWidth={0.25}
        sx={{ cursor: 'pointer', fontSize: '16px' }}
      />
      {notificationsUnreadCount ? (
        <Box
          sx={{
            backgroundColor: '#D7335C',
            borderRadius: '4px',
            height: '8px',
            marginLeft: '-8px',
            marginTop: '-10px',
            width: '8px',
          }}
        />
      ) : null}
      <Dialog
        BackdropProps={{ sx: { backgroundColor: 'transparent' } }}
        PaperProps={{
          sx: {
            borderRadius: '10px',
            boxShadow: '-1px 1px 2px rgba(0, 0, 0, 0.25)',
            margin: 0,
            padding: '10px 15px',
            position: 'fixed',
            right: '10px',
            top: '10px',
            width: '320px',
          },
        }}
        onBackdropClick={() => {
          setNotificationsListOpen(false);
        }}
        open={!!newNotification || isNotificationsListOpen}
      >
        {isNotificationsListOpen ? (
          <UnreadNotificationsList
            onClickItem={() => {
              setNotificationsListOpen(false);
            }}
            unreadCount={notificationsUnreadCount}
          />
        ) : newNotification ? (
          <NotificationItem notification={newNotification} />
        ) : null}
      </Dialog>
    </Box>
  );
};
